import React from "react";

function joinNames(names) {
  if (!names || names.length === 0) return "None yet";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** One BU summary card — big active-project count, mini monthly-load bar
 * chart, and team allocation chips. Mirrors the reference planner's card
 * layout exactly (colored dot + label, waitlist chip, "Next up" line). */
export default function PlannerSummaryCard({ card }) {
  const maxCount = Math.max(1, ...card.months.map((m) => m.count));
  const label = card.bu === "Strategy" ? "Strategy team" : card.bu;
  return (
    <div className="card-flat p-4 flex flex-col gap-2" data-testid={`planner-card-${card.bu}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: card.color }} />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        </div>
        {card.waitlist_count > 0 && (
          <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid={`planner-card-waitlist-${card.bu}`}>
            +{card.waitlist_count} waitlisted
          </span>
        )}
      </div>

      <div>
        <span className="font-display text-3xl font-bold text-slate-900">{card.active_count}</span>
        <span className="text-slate-400 text-xs ml-1">active projects</span>
      </div>
      <div className="text-[11px] text-slate-500 leading-snug truncate" title={joinNames(card.active_names)}>{joinNames(card.active_names)}</div>

      <div className="text-[11px] text-slate-500">{card.running_today} running today</div>
      <div className="text-[11px] text-slate-500 truncate">
        Next up: {card.next_up.names.length ? `${joinNames(card.next_up.names)}${card.next_up.date ? ` on ${new Date(card.next_up.date).toLocaleDateString([], { month: "short", day: "numeric" })}` : ""}` : "—"}
      </div>

      <div className="flex items-end gap-0.5 h-8 mt-1">
        {card.months.map((m) => (
          <div key={m.month} className="flex-1 rounded-t-sm" style={{ height: `${Math.max(6, (m.count / maxCount) * 100)}%`, background: card.color, opacity: m.count ? 0.85 : 0.15 }} title={`${m.month}: ${m.count} running`} />
        ))}
      </div>

      {card.team.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {card.team.map((t) => (
            <span key={t.user_id} className="text-[9.5px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-full" data-testid={`planner-card-chip-${card.bu}-${t.user_id}`}>
              {t.name} {t.allocation_pct}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
